import { useEffect, useRef, useState } from "react";
import {
  Check,
  List,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  X,
} from "lucide-react";
import { isSafari } from "react-device-detect";

import * as Api from "@modules/api";
import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import BasicSlider from "@components/items/display/Slider";
import { useAudioPlayer } from "@modules/content/AudioPlayerContext";
import { convertMsToHourMinSec } from "@utils/helpers/timeFormtter";
import { Select } from "@ui/select";
import { Menu } from "@components/items/display/Menu";
import { CircleProgress } from "@components/items/display/CircleProgress";
import { useSelectedPlayerContent } from "@modules/content/hooks/useSelectedPlayerContent";
import {
  ItemContentOutputWithProgress,
  SelectedPlayerContentSource,
} from "@lib/stores/content/types";
import { useDarkMode } from "@modules/theme/DarkModeContext";
import { useContentSelector } from "@modules/content/hooks/useContentSelector";
import {
  getSelectedPlayerContentEntityItemsById,
  setAlert,
  useAppDispatch,
  useAppSelector,
} from "@lib/stores";
import { useAppLiveQuery } from "@modules/local/hooks";
import { db } from "@modules/local/db";
import { usePageData } from "@modules/content/hooks/usePageData";
import { ContentDetailsParameters } from "../content/BookSummary";
import { useGetNextContent } from "@lib/stores/content/hooks";
import {
  getStorageSync,
  removeStorage,
  setStorage,
} from "@modules/storage/storageHelper";
import { useLockedContents } from "@modules/content/hooks/useLockedContent";
import { AlertType } from "@lib/stores/common/types";
import { translate } from "@modules/localizations/translate";
import { getQueryFromUrl } from "@utils/helpers/getQueryFromUrl";
import { useB2B } from "@modules/content/B2BContext";
import { SVG } from "@components/items/common/SVG";
import { NarratorMenu } from "@components/items/display/NarratorMenu";
import { Server } from "@modules/api";

export const MainPlayer = () => {
  const playbackRates = [
    { label: "0.5x", value: 0.5 },
    { label: "0.75x", value: 0.75 },
    { label: "1.0x", value: 1 },
    { label: "1.25x", value: 1.25 },
    { label: "1.5x", value: 1.5 },
    { label: "1.75x", value: 1.75 },
    { label: "2.0x", value: 2 },
  ];

  const nextItemSource = getQueryFromUrl("nextItemSource");
  const playlistId = getQueryFromUrl("playlistId");

  const latestPlayingContent = getStorageSync("LATEST_PLAYING_CONTENT");
  const currentVoiceOver = latestPlayingContent?.voiceOvers?.currentVoiceOver;

  const [sliderValueTemp, setSliderValueTemp] = useState<number | null>(null);
  const [selectedNarrator, setSelectedNarrator] = useState<number | undefined>(
    currentVoiceOver?.narratorId
  );
  const [commitedSliderValue, setCommitedSliderValue] = useState<number | null>(
    null
  );
  const [sliderValue, setSliderValue] = useState<number | null>(null);
  const [chaptersWithOptimisticProgress, setChaptersWithOptimisticProgress] =
    useState<ItemContentOutputWithProgress[]>([]);
  const [lastChapterEnded, setLastChapterEnded] = useState(false);
  const [
    firstChapterOfFirstChapterLockedContentEnded,
    setFirstChapterOfFirstChapterLockedContentEnded,
  ] = useState(false);

  const user = useAppSelector((state) => state.auth.user);
  const voiceOvers = useAppSelector(
    (state) => state?.content?.contentVoiceOvers?.data
  );

  const userId = user.id || "";
  const nextTitleDisabled = user.nextTitleDisabled;

  const { result: flow } = useAppLiveQuery(
    () => db.readerFlows.get(userId),
    [userId]
  );

  const { data: nextContentData } = useGetNextContent();

  const { b2B } = useB2B();

  const navigationPage = flow?.typeNavigations?.find(
    (tnv) => tnv.contentEntityTypeId === nextContentData?.contentEntityTypeId
  );

  const { getParameterValues: getParameterValue } =
    usePageData<ContentDetailsParameters>({
      pageId: navigationPage?.detailsPageId ?? "",
      context: nextContentData || undefined,
    });

  const item = getParameterValue([
    "aboutTitle",
    "about",
    "authors",
    "description",
    "descriptionTitle",
    "recommendedTo",
    "recommendedToTitle",
    "readingIcon",
    "readingTitle",
    "listeningIcon",
    "listeningTitle",
    "thumbnail",
    "authorsTitle",
    "categories",
  ]);

  const {
    chapters,
    isEnded,
    onPlayingChapterSelect,
    onPlaying,
    onSeek,
    onNextChapterStart,
    onPause,
    onStop,
    onStartPlaying,
    playingChapterId,
    firstPlayingChapterId,
    selectedPlayerContent,
    onNextContentGet,
    nextContent,
  } = useSelectedPlayerContent();

  const playingChapterIndex = chapters.findIndex(
    (c) => c.id === playingChapterId
  );

  const {
    togglePlayer,
    load,
    unload,
    play,
    pause,
    seek,
    isPlaying,
    duration,
    currentTime,
    changePlaybackRate,
    playbackRate,
    isAutoStart,
  } = useAudioPlayer();

  const { appliedTheme } = useDarkMode();

  const { onSelectContent } = useContentSelector();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentTimeRef = useRef(currentTime);
  const durationRef = useRef(duration);

  const selectedTime = sliderValue !== null ? sliderValue * duration : null;

  const chaptersLength = chapters.length;

  const selectedChapterSignedUrl = chapters.find(
    (c) => c.id === playingChapterId
  )?.signedAudioUrl;

  const selectedAudioReference = chapters.find(
    (c) => c.id === playingChapterId
  )?.audioReference;

  const firstPlayingChapterProgress = chapters.find(
    (c) => c.id === firstPlayingChapterId
  )?.progress;

  const playingChapterOptimisticProgress = chaptersWithOptimisticProgress.find(
    (c) => c.id === playingChapterId
  )?.progress;

  const onAudioSelect = (id: string | undefined) => {
    unload();

    setLastChapterEnded(false);

    const selectedChapterSignedUrl = chapters.find(
      (c) => c.id === id
    )?.signedAudioUrl;

    if (selectedChapterSignedUrl) {
      setTimeout(() => {
        load(selectedChapterSignedUrl);
      }, 100);
    }
  };

  const { isLockedContent } = useLockedContents(
    (latestPlayingContent?.content
      ? [latestPlayingContent.content]
      : []
    ).concat(nextContent.content ? [nextContent.content] : [])
  );
  const lockedContents = isLockedContent.result;
  const isLatestPlayingContentLocked = lockedContents?.find(
    (l) => l.id === latestPlayingContent?.content.id
  )?.isLocked;
  const isNextContentLocked = lockedContents?.find(
    (l) => l.id === nextContent?.content?.id
  )?.isLocked;
  const selectedPlayerContentLockType = lockedContents?.find(
    (l) => l.id === selectedPlayerContent.id
  )?.lockType;

  useEffect(() => {
    if (
      latestPlayingContent &&
      (selectedPlayerContent.source === undefined ||
        selectedPlayerContent.source ===
          SelectedPlayerContentSource.OnRefresh) &&
      b2B?.isB2B !== undefined
    ) {
      onSelectContent(latestPlayingContent.content, latestPlayingContent.item, {
        forcedAutoPlayDisabled: true,
        isLocked: isLatestPlayingContentLocked,
        skipDetailsPageCheck: true,
        source: SelectedPlayerContentSource.OnRefresh,
        nextItemSource: nextItemSource ? Number(nextItemSource) : undefined,
        playlistId: playlistId ? Number(playlistId) : undefined,
      });
    }
  }, [
    latestPlayingContent?.content.id,
    isLatestPlayingContentLocked,
    selectedPlayerContent.source,
    nextItemSource,
    playlistId,
    b2B?.isB2B,
  ]);

  useEffect(() => {
    currentTimeRef.current = currentTime;
    durationRef.current = duration;
  }, [currentTime, duration]);

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        onPlaying(currentTimeRef.current / durationRef.current);
      }, 15000);
    }

    return () => {
      if (intervalRef.current && isPlaying) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    if (isEnded && playingChapterIndex < chaptersLength - 1) {
      if (
        selectedPlayerContentLockType !== Api.Server.LockType.FirstChapterFree
      ) {
        onPlayingChapterSelect(
          chapters[Math.min(playingChapterIndex + 1, chaptersLength - 1)].id,
          () => {
            onAudioSelect(
              chapters[Math.min(playingChapterIndex + 1, chaptersLength - 1)].id
            );
          }
        );
      } else {
        setFirstChapterOfFirstChapterLockedContentEnded(true);

        selectedChapterSignedUrl && load(selectedChapterSignedUrl);
      }

      onNextChapterStart(
        chapters[playingChapterIndex].id,
        Api.Server.PlayingMode.MiniPlayer,
        1
      );
    } else if (
      isEnded &&
      selectedChapterSignedUrl &&
      playingChapterIndex === chaptersLength - 1
    ) {
      setLastChapterEnded(true);

      if (!nextTitleDisabled) {
        onNextContentGet(() => {
          if (nextContent.content) {
            onSelectContent(nextContent.content, item, {
              isLocked: isNextContentLocked,
              skipDetailsPageCheck: true,
              source: SelectedPlayerContentSource.NextTitle,
              nextItemSource: nextItemSource
                ? Number(nextItemSource)
                : undefined,
              playlistId: playlistId ? Number(playlistId) : undefined,
            });
          } else {
            load(selectedChapterSignedUrl);
          }
        }, isNextContentLocked);
      } else {
        load(selectedChapterSignedUrl);
      }
    }
  }, [
    isEnded,
    chaptersLength,
    nextTitleDisabled,
    nextContent.content?.id,
    navigationPage?.detailsPageId,
    isNextContentLocked,
    nextItemSource,
    playlistId,
    selectedPlayerContentLockType,
  ]);

  useEffect(() => {
    if (duration) {
      if (firstChapterOfFirstChapterLockedContentEnded) {
        seek(duration);

        return;
      }

      if (
        firstPlayingChapterProgress !== undefined &&
        firstPlayingChapterId === playingChapterId &&
        !lastChapterEnded
      ) {
        seek(
          firstPlayingChapterProgress !== 1
            ? firstPlayingChapterProgress * duration
            : 0
        );

        if (isAutoStart) {
          play(() => {
            onStartPlaying(firstPlayingChapterProgress);
          });
        }
      } else if (
        playingChapterOptimisticProgress !== undefined &&
        (playingChapterIndex !== chaptersLength - 1 ||
          (playingChapterIndex === chaptersLength - 1 && !lastChapterEnded))
      ) {
        seek(
          playingChapterOptimisticProgress !== 100
            ? (playingChapterOptimisticProgress / 100) * duration
            : 0
        );

        play(() => {
          onStartPlaying(playingChapterOptimisticProgress / 100);
        });
      }
    }
  }, [
    duration,
    firstPlayingChapterId,
    isAutoStart,
    firstPlayingChapterProgress,
    chaptersLength,
    lastChapterEnded,
  ]);

  useEffect(() => {
    setChaptersWithOptimisticProgress(
      chapters.map((c) => {
        return {
          ...c,
          progress: Number(((c.progress || 0) * 100).toFixed()),
        };
      })
    );
  }, [chapters.length]);

  useEffect(() => {
    setChaptersWithOptimisticProgress((chapters) => {
      return chapters.map((c) => {
        return {
          ...c,
          progress:
            duration && playingChapterId === c.id
              ? Number(((currentTime / duration) * 100).toFixed(0))
              : c.progress,
          isCompleted: c.isCompleted
            ? c.isCompleted
            : currentTime / duration > 0.95 && playingChapterId === c.id
              ? true
              : false,
        };
      });
    });
  }, [playingChapterId, currentTime, duration]);

  useEffect(() => {
    console.log(sliderValueTemp, commitedSliderValue, duration);

    if (sliderValueTemp !== null) {
      if (
        commitedSliderValue !== null &&
        Math.floor(sliderValueTemp * 100) / 10 ===
          Math.floor(commitedSliderValue * 100) / 10
      ) {
        setSliderValueTemp(null);

        const time = commitedSliderValue * duration;

        seek(time, () => {
          setSliderValue(null);
          setCommitedSliderValue(null);
          setFirstChapterOfFirstChapterLockedContentEnded(false);

          if (duration) {
            onSeek(time / duration);
          }
        });
      } else {
        setSliderValue(sliderValueTemp);
      }
    } else if (commitedSliderValue !== null) {
      const time = commitedSliderValue * duration;

      seek(time, () => {
        setSliderValue(null);
        setCommitedSliderValue(null);
        setFirstChapterOfFirstChapterLockedContentEnded(false);

        if (duration) {
          onSeek(time / duration);
        }
      });
    }
  }, [sliderValueTemp, commitedSliderValue, duration]);

  const PlayIcon = () => {
    return (
      <Play
        onClick={() => {
          if (
            isLatestPlayingContentLocked ||
            (isNextContentLocked &&
              selectedPlayerContent.source ===
                SelectedPlayerContentSource.NextTitle) ||
            (firstChapterOfFirstChapterLockedContentEnded &&
              selectedPlayerContentLockType ===
                Api.Server.LockType.FirstChapterFree)
          ) {
            setAlert({
              show: true,
              for: "page",
              type: AlertType.WARNING,
              messages: [
                {
                  code: 0,
                  field: null,
                  message: translate(
                    "content-details.messages.subscribe to listen"
                  ),
                },
              ],
            });

            return;
          }

          play(() => {
            onStartPlaying(currentTime / duration);
          });
        }}
        className="h-8 w-8 cursor-pointer fill-black text-black dark:fill-white dark:text-white"
      />
    );
  };

  return (
    <div
      className="relative flex h-full w-full items-center justify-center gap-3 border bg-white p-3 text-black dark:bg-black dark:text-white"
      dir="rtl"
    >
      <div
        className="absolute right-5 top-1/2 -translate-y-1/2 cursor-pointer"
        onClick={() => {
          if (duration) {
            onStop(currentTime / duration);
          }

          unload();
          togglePlayer();

          removeStorage("LATEST_PLAYING_CONTENT");
        }}
      >
        <X />
      </div>
      <div className="relative">
        <Menu
          items={chaptersWithOptimisticProgress.map((c) => {
            return {
              key: c.id || "",
              value: (
                <div
                  className={`flex w-full max-w-[350px] items-center justify-between gap-10 ${
                    c.isLocked ? "opacity-50" : ""
                  }`}
                >
                  <div className="flex items-center justify-start gap-2">
                    <img
                      src={c.fullImageUrl || selectedPlayerContent.thumbnail}
                      alt={c.title}
                      className="h-[40px] w-[40px] rounded-[4px]"
                    />
                    <div>{c.title}</div>
                  </div>
                  <div>
                    <CircleProgress
                      value={c.progress}
                      trackColor={appliedTheme === "dark" ? "white" : "black"}
                      trailColor={appliedTheme !== "dark" ? "white" : "black"}
                      bgColor={appliedTheme !== "dark" ? "white" : "black"}
                      className="h-[20px] w-[20px] text-[11px]"
                      isValueShown={false}
                      customValue={
                        c.isCompleted ? (
                          <div className="text-black dark:text-white">
                            <Check size={14} strokeWidth={3} />
                          </div>
                        ) : (
                          ""
                        )
                      }
                    />
                  </div>
                </div>
              ),
              action: (key, index) => {
                !c.isLocked &&
                  onPlayingChapterSelect(key, () => {
                    onAudioSelect(key);
                  });
              },
            };
          })}
          label={
            <List
              className="rotate-180 cursor-pointer"
              size={55}
              strokeWidth={"2.5px"}
            />
          }
          contentClassName="bg-white dark:bg-black h-[500px]"
        />
      </div>
      {voiceOvers && voiceOvers?.length > 1 && (
        <div className="relative">
          <NarratorMenu
            items={voiceOvers.map((c) => {
              return {
                key: c.narratorId || 0,
                value: <div>{c.narrator}</div>,
                action: (key, index) => {
                  getSelectedPlayerContentEntityItemsById(
                    {
                      contentEntityId: latestPlayingContent?.content.id,
                      narratorId: c.narratorId,
                    },
                    (res) => {
                      const currentPosition = currentTime / duration;

                      setChaptersWithOptimisticProgress(res);
                      setSelectedNarrator(key);

                      const currentContent = getStorageSync(
                        "LATEST_PLAYING_CONTENT"
                      ) || {
                        pageId: "",
                        content: {} as Server.ContentEntityOutput,
                        item: {},
                      };

                      setStorage({
                        key: "LATEST_PLAYING_CONTENT",
                        data: {
                          pageId: currentContent.pageId,
                          content: currentContent.content,
                          item: currentContent.item,
                          voiceOvers: {
                            data: voiceOvers,
                            currentVoiceOver: c,
                          },
                        },
                      });

                      const currentChapter = res.find(
                        (ch) => ch.id === playingChapterId
                      );

                      if (currentChapter?.signedAudioUrl) {
                        unload();
                        setTimeout(() => {
                          load(currentChapter?.signedAudioUrl ?? "");

                          setTimeout(() => {
                            if (duration) {
                              seek(currentPosition * duration, () => {
                                if (isPlaying) {
                                  play(() => {
                                    onStartPlaying(currentPosition);
                                  });
                                }
                              });
                            }
                          }, 300);
                        }, 100);
                      }
                    }
                  );
                },
              };
            })}
            selectedItem={selectedNarrator}
            label={
              <div>
                <SVG
                  src="/assets/icons/narrator.svg"
                  preProcessor={(code: string) =>
                    code
                      .replace(
                        /(fill=)"(?!none")[^"]+"/g,
                        'fill="currentColor"'
                      )
                      .replace(
                        /(stroke=)"(?!none")[^"]+"/g,
                        'stroke="currentColor"'
                      )
                  }
                  className="h-[30px] w-[30px] dark:text-white"
                />
              </div>
            }
            contentClassName="bg-white dark:bg-[#010D19] h-fit border-none"
          />
        </div>
      )}
      {/* <div>
        <Heart
          className={`cursor-pointer text-primary ${
            isFavorit && "fill-primary"
          }`}
          onClick={() => setIsFavorit(!isFavorit)}
        />
      </div> */}
      <div className="flex items-center justify-center gap-3">
        <img
          src={selectedPlayerContent.thumbnail}
          alt="daily-content"
          width={55}
          height={55}
        />
        <div className="flex flex-col items-start justify-center">
          <div className="text-[14px] font-medium">
            {selectedPlayerContent.title}
          </div>
          <div className="text-[12px]">{selectedPlayerContent.author}</div>
        </div>
      </div>
      <div className="w-[500px] space-y-2 self-end">
        <BasicSlider
          trackClassName="h-1 cursor-pointer bg-black dark:bg-white"
          thumbClassName="h-3 w-3 cursor-pointer"
          rangeClassName={
            !Boolean(sliderValueTemp) && !isSafari
              ? "transition-all ease-linear duration-300"
              : "transition-none"
          }
          className={
            !Boolean(sliderValueTemp) && !isSafari
              ? "*:transition-all *:duration-300 *:ease-linear"
              : "*:transition-none"
          }
          min={0}
          max={1}
          step={0.01}
          value={[sliderValue !== null ? sliderValue : currentTime / duration]}
          onValueChange={(value) => {
            console.log(value, "change");

            const timePercent = value[0];

            setSliderValueTemp(timePercent);
          }}
          onValueCommit={(value) => {
            console.log(value, "commit");

            const timePercent = value[0];

            setCommitedSliderValue(timePercent);
          }}
        />
        <div className="flex items-center justify-between">
          <div>{convertMsToHourMinSec(duration * 1000)}</div>
          <div>
            {selectedTime !== null
              ? convertMsToHourMinSec(selectedTime * 1000)
              : convertMsToHourMinSec(currentTime * 1000)}
          </div>
        </div>
      </div>
      <div className="cursor-pointer">
        <Select
          items={playbackRates.map((p) => {
            return {
              ...p,
              value: p.value.toString(),
            };
          })}
          defaultValue={playbackRate.toString()}
          onValueChange={(value) => {
            changePlaybackRate(Number(value));
          }}
        />
      </div>
      <div className="flex items-center justify-around gap-5">
        <Forward10Icon
          className="cursor-pointer"
          onClick={() => {
            const time = currentTime + 10;

            seek(time, () => {
              if (duration) {
                onSeek(time / duration);
              }
            });
          }}
        />
        <SkipForward
          className="h-5 w-5 cursor-pointer fill-black text-black dark:fill-white dark:text-white"
          onClick={() => {
            onPlayingChapterSelect(
              chapters[Math.min(playingChapterIndex + 1, chapters.length - 1)]
                .id,
              () => {
                onAudioSelect(
                  chapters[
                    Math.min(playingChapterIndex + 1, chapters.length - 1)
                  ].id
                );
              }
            );

            onNextChapterStart(
              chapters[playingChapterIndex].id,
              Api.Server.PlayingMode.MiniPlayer,
              currentTime / duration
            );
          }}
        />
        {duration ? (
          isPlaying ? (
            <Pause
              onClick={() => {
                pause(() => {
                  onPause(currentTime / duration);
                });
              }}
              className="h-8 w-8 cursor-pointer fill-black text-black dark:fill-white dark:text-white"
            />
          ) : (
            <PlayIcon />
          )
        ) : !isEnded &&
          (isLatestPlayingContentLocked === false ||
            isNextContentLocked === false) ? (
          <div className="h-8 w-8 animate-spin rounded-full border-[2px] border-y-black border-l-transparent border-r-black dark:border-y-white dark:border-r-white"></div>
        ) : (
          <PlayIcon />
        )}
        <SkipBack
          className="h-5 w-5 cursor-pointer fill-black text-black dark:fill-white dark:text-white"
          onClick={() => {
            onPlayingChapterSelect(
              chapters[Math.max(playingChapterIndex - 1, 0)].id,
              () => {
                onAudioSelect(
                  chapters[Math.max(playingChapterIndex - 1, 0)].id
                );
              }
            );
          }}
        />
        <Replay10Icon
          className="cursor-pointer"
          onClick={() => {
            const time = currentTime - 10;

            seek(time, () => {
              if (duration) {
                onSeek(time / duration);
              }
            });
          }}
        />
      </div>
    </div>
  );
};
